import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, BRAND } from "../lib/config";
import { Button } from "../components/ui/button";
import {
  CheckCircle2, Loader2, XCircle, Calendar, MapPin, Phone,
  MessageSquare, ArrowRight, Sparkles, Share2,
} from "lucide-react";
import Logo from "../components/Logo";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const isSetup = params.get("setup") === "1"; // iter185
  const [state, setState] = useState({ status: "checking", payment_status: "", amount_total: 0 });
  const [booking, setBooking] = useState(null);
  const [timedOut, setTimedOut] = useState(false);
  const [setupResult, setSetupResult] = useState(null);
  const attempts = useRef(0);
  const timer = useRef(null);

  // Restore cached booking for the receipt UI
  useEffect(() => {
    try {
      const cached = sessionStorage.getItem("last_booking");
      if (cached) setBooking(JSON.parse(cached));
    } catch { /* ignore */ }
  }, []);

  // iter185 — Setup-mode finalize: call the backend to save the card + promote intent.
  useEffect(() => {
    if (!sessionId || !isSetup) return;
    let cancelled = false;
    const finalize = async () => {
      try {
        const { data } = await api.post("/payments/setup/finalize", { session_id: sessionId });
        if (!cancelled) setSetupResult(data);
      } catch (e) {
        if (!cancelled) setSetupResult({ ok: false, error: e?.response?.data?.detail || "Setup finalize failed" });
      }
    };
    finalize();
    return () => { cancelled = true; };
  }, [sessionId, isSetup]);

  useEffect(() => {
    if (!sessionId || isSetup) return; // Skip legacy polling on setup path
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/checkout/status/${sessionId}`);
        setState(data);
        if (data.payment_status === "paid" || data.status === "expired") return;
        if (attempts.current < 12) {
          attempts.current++;
          timer.current = setTimeout(poll, 2000);
        } else {
          setTimedOut(true);
        }
      } catch {
        if (attempts.current < 5) {
          attempts.current++;
          timer.current = setTimeout(poll, 2500);
        } else {
          setTimedOut(true);
        }
      }
    };
    poll();
    return () => clearTimeout(timer.current);
  }, [sessionId]);

  const paid = state.payment_status === "paid";
  const expired = state.status === "expired";
  const amount = state.amount_total ? (state.amount_total / 100).toFixed(2) : (booking?.price ? Number(booking.price).toFixed(2) : "—");

  // iter185 — SETUP SUCCESS: card was saved via SetupIntent, booking promoted
  // to autopay_scheduled. No charge yet — customer will be charged after
  // each completed visit.
  if (isSetup && setupResult?.ok) {
    return (
      <div className="min-h-screen bg-[#F4F6F5] pb-16" data-testid="payment-success-page">
        <div className="relative overflow-hidden bg-gradient-to-br from-[#062818] via-[#0A3D27] to-[#047857] text-white">
          <div className="relative max-w-2xl mx-auto px-4 sm:px-6 pt-10 pb-14 text-center">
            <div className="flex justify-center mb-4 opacity-90">
              <Logo size={48} />
            </div>
            <div className="relative inline-flex items-center justify-center mt-2">
              <div className="absolute inset-0 rounded-full bg-emerald-400/20 animate-ping" />
              <div className="relative w-24 h-24 sm:w-28 sm:h-28 rounded-full bg-white text-[#047857] flex items-center justify-center shadow-[0_20px_50px_rgba(16,185,129,0.45)]">
                <CheckCircle2 className="w-14 h-14 sm:w-16 sm:h-16" strokeWidth={2.4} />
              </div>
            </div>
            <div className="mt-6 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-400/20 border border-emerald-300/30 text-emerald-100 text-[10px] uppercase tracking-[0.22em] font-extrabold">
              <Sparkles className="w-3 h-3" /> AutoPay scheduled
            </div>
            <h1 className="mt-4 text-4xl sm:text-5xl font-extrabold tracking-tighter font-[Outfit] leading-[1.02]" data-testid="setup-success">
              You're all set!
            </h1>
            <p className="mt-3 text-emerald-100/85 text-sm sm:text-base max-w-md mx-auto">
              Your booking is confirmed. Your card{setupResult.card_last4 ? ` ending in ${setupResult.card_last4}` : ""} is saved for AutoPay. You'll be charged automatically after each completed visit — never before.
            </p>
          </div>
        </div>
        <div className="max-w-2xl mx-auto px-4 sm:px-6 -mt-8">
          <div className="bg-white rounded-3xl border border-gray-200 shadow-[0_20px_50px_rgba(6,40,24,0.08)] overflow-hidden p-6 sm:p-8">
            <div className="text-[11px] uppercase tracking-[0.18em] text-gray-500 font-bold">Payment authorization</div>
            <p className="mt-2 text-sm text-gray-700 leading-relaxed">
              By completing this booking, you authorized JDSN Lawn Care to securely save your payment method and automatically charge it after each completed service until you cancel or update your payment method. No charge is made until a visit is completed.
            </p>
            {booking && (
              <div className="mt-4 space-y-3">
                <Row label="Service" value={booking.service_name} />
                <Row label="When" value={`${booking.preferred_date || "—"} · ${booking.preferred_time || ""}`} icon={<Calendar className="w-4 h-4 text-[#047857]" />} />
                <Row label="Where" value={`${booking.address || ""}, ${booking.area || ""}`} icon={<MapPin className="w-4 h-4 text-[#047857]" />} />
                {booking.frequency_label && <Row label="Frequency" value={booking.frequency_label} />}
                <Row label="Booking ID" value={<code className="text-xs">{setupResult.booking_id || booking.booking_id}</code>} />
              </div>
            )}
          </div>
          <div className="mt-5">
            <Link to="/dashboard" className="block">
              <Button className="w-full rounded-full h-14 bg-[#047857] hover:bg-[#065F46] text-white font-extrabold text-base" data-testid="setup-success-dashboard-btn">
                Go to dashboard <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (isSetup && setupResult && !setupResult.ok) {
    return (
      <div className="min-h-[80vh] bg-[#F4F6F5] flex items-center justify-center px-4 py-12" data-testid="payment-success-page">
        <div className="max-w-md w-full bg-white rounded-3xl border border-gray-200 p-8 text-center shadow-sm">
          <XCircle className="w-16 h-16 text-red-500 mx-auto" />
          <h1 className="mt-5 text-2xl font-extrabold tracking-tight text-gray-900 font-[Outfit]">Card save failed</h1>
          <p className="mt-2 text-sm text-gray-600">{setupResult.error || "Your booking was not saved. Please try again."}</p>
          <Link to="/book">
            <Button className="mt-6 rounded-full h-12 px-7 bg-[#047857] hover:bg-[#065F46]">Retry booking</Button>
          </Link>
        </div>
      </div>
    );
  }

  if (isSetup && !setupResult) {
    return (
      <div className="min-h-[80vh] bg-[#F4F6F5] flex items-center justify-center px-4 py-12" data-testid="payment-success-page">
        <div className="max-w-md w-full bg-white rounded-3xl border border-gray-200 p-10 text-center shadow-sm">
          <Loader2 className="w-10 h-10 animate-spin text-[#10B981] mx-auto" />
          <h1 className="mt-6 text-2xl font-extrabold tracking-tight text-gray-900 font-[Outfit]">Saving your card…</h1>
          <p className="mt-2 text-sm text-gray-600">This usually takes a few seconds.</p>
        </div>
      </div>
    );
  }

  const share = async () => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: "JDSN Lawn Care",
          text: `I just booked JDSN Lawn Care — Jacksonville's premium lawn service.`,
          url: window.location.origin,
        });
      }
    } catch { /* user cancelled */ }
  };

  // === EXPIRED / FAILED ===
  if (expired) {
    return (
      <div className="min-h-[80vh] bg-[#F4F6F5] flex items-center justify-center px-4 py-12" data-testid="payment-success-page">
        <div className="max-w-md w-full bg-white rounded-3xl border border-gray-200 p-8 text-center shadow-sm">
          <XCircle className="w-16 h-16 text-red-500 mx-auto" />
          <h1 className="mt-5 text-2xl font-extrabold tracking-tight text-gray-900 font-[Outfit]">Session expired</h1>
          <p className="mt-2 text-sm text-gray-600">No worries — your booking is still saved. Try again from your dashboard.</p>
          <Link to="/dashboard">
            <Button className="mt-6 rounded-full h-12 px-7 bg-[#047857] hover:bg-[#065F46]">
              Back to dashboard
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  // === PENDING ===
  if (!paid) {
    return (
      <div className="min-h-[80vh] bg-[#F4F6F5] flex items-center justify-center px-4 py-12" data-testid="payment-success-page">
        <div className="max-w-md w-full bg-white rounded-3xl border border-gray-200 p-10 text-center shadow-sm">
          <div className="w-20 h-20 mx-auto rounded-full bg-emerald-50 flex items-center justify-center">
            {timedOut ? (
              <Loader2 className="w-10 h-10 text-amber-500" />
            ) : (
              <Loader2 className="w-10 h-10 animate-spin text-[#10B981]" />
            )}
          </div>
          <h1 className="mt-6 text-2xl font-extrabold tracking-tight text-gray-900 font-[Outfit]">
            {timedOut ? "Still processing…" : "Confirming payment…"}
          </h1>
          <p className="mt-2 text-sm text-gray-600">
            {timedOut
              ? "Your payment is taking longer than usual. Check your email for a Stripe receipt, or head to your dashboard — we'll update it when payment clears."
              : "Don't close this page. This usually takes a few seconds."}
          </p>
          {timedOut && (
            <Link to="/dashboard">
              <Button className="mt-5 rounded-full h-12 px-6 bg-[#047857] hover:bg-[#065F46]">
                Go to dashboard
              </Button>
            </Link>
          )}
        </div>
      </div>
    );
  }

  // === PAID — BRANDED SUCCESS ===
  return (
    <div className="min-h-screen bg-[#F4F6F5] pb-16" data-testid="payment-success-page">
      {/* HERO — green gradient with branded check */}
      <div className="relative overflow-hidden bg-gradient-to-br from-[#062818] via-[#0A3D27] to-[#047857] text-white">
        <div className="absolute inset-0 opacity-[0.12]" style={{
          backgroundImage: "radial-gradient(circle at 20% 10%, #34D399 0, transparent 35%), radial-gradient(circle at 80% 80%, #10B981 0, transparent 35%)",
        }} />

        <div className="relative max-w-2xl mx-auto px-4 sm:px-6 pt-10 pb-14 text-center">
          <div className="flex justify-center mb-4 opacity-90">
            <Logo size={48} />
          </div>

          {/* Animated check */}
          <div className="relative inline-flex items-center justify-center mt-2">
            <div className="absolute inset-0 rounded-full bg-emerald-400/20 animate-ping" />
            <div className="absolute inset-0 rounded-full bg-emerald-400/10 scale-150" />
            <div className="relative w-24 h-24 sm:w-28 sm:h-28 rounded-full bg-white text-[#047857] flex items-center justify-center shadow-[0_20px_50px_rgba(16,185,129,0.45)]">
              <CheckCircle2 className="w-14 h-14 sm:w-16 sm:h-16" strokeWidth={2.4} />
            </div>
          </div>

          <div className="mt-6 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-400/20 border border-emerald-300/30 text-emerald-100 text-[10px] uppercase tracking-[0.22em] font-extrabold">
            <Sparkles className="w-3 h-3" /> Payment received
          </div>

          <h1 className="mt-4 text-4xl sm:text-5xl font-extrabold tracking-tighter font-[Outfit] leading-[1.02]" data-testid="payment-paid">
            Thank you!
          </h1>
          <p className="mt-3 text-emerald-100/85 text-sm sm:text-base max-w-md mx-auto">
            Your payment of <span className="font-extrabold text-white tabular-nums">${amount}</span> was successful.
            Your booking is confirmed — we'll see you on the date you selected.
          </p>
        </div>
      </div>

      {/* RECEIPT CARD */}
      <div className="max-w-2xl mx-auto px-4 sm:px-6 -mt-8">
        <div className="bg-white rounded-3xl border border-gray-200 shadow-[0_20px_50px_rgba(6,40,24,0.08)] overflow-hidden">
          <div className="p-6 sm:p-8 border-b border-dashed border-gray-200">
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-[#047857] font-extrabold">Receipt</div>
                <div className="mt-1 font-bold text-gray-900">{BRAND.name}</div>
              </div>
              <div className="text-right">
                <div className="text-[10px] uppercase tracking-[0.18em] text-gray-500 font-bold">Amount paid</div>
                <div className="text-2xl font-extrabold text-[#047857] tabular-nums font-[Outfit]">${amount}</div>
              </div>
            </div>
          </div>

          {booking && (
            <div className="p-6 sm:p-8 space-y-4">
              <Row label="Service" value={booking.service_name} />
              <Row
                label="When"
                value={`${booking.preferred_date || "—"} · ${booking.preferred_time || ""}`}
                icon={<Calendar className="w-4 h-4 text-[#047857]" />}
              />
              <Row
                label="Where"
                value={`${booking.address || ""}, ${booking.area || ""}`}
                icon={<MapPin className="w-4 h-4 text-[#047857]" />}
              />
              {booking.yard_area_label && <Row label="Yard area" value={booking.yard_area_label} />}
              {booking.frequency_label && <Row label="Frequency" value={booking.frequency_label} />}
              <Row label="Booking ID" value={<code className="text-xs">{booking.booking_id}</code>} />
            </div>
          )}

          <div className="px-6 sm:px-8 pb-6 sm:pb-8">
            <div className="rounded-2xl bg-[#F4F6F5] border border-gray-200 p-4 text-xs text-gray-600 leading-relaxed">
              A confirmation has been emailed to{" "}
              <span className="font-bold text-gray-900">{booking?.email || "your inbox"}</span>{" "}
              and an SMS reminder will arrive 24 hours before your appointment.
            </div>
          </div>
        </div>

        {/* CTA Row */}
        <div className="mt-5 grid sm:grid-cols-2 gap-3">
          <Link to="/dashboard" className="block">
            <Button className="w-full rounded-full h-14 bg-[#047857] hover:bg-[#065F46] text-white font-extrabold text-base" data-testid="success-dashboard-btn">
              Go to dashboard <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
          <div className="grid grid-cols-3 gap-2">
            <a href={`tel:${BRAND.phoneTel}`} className="h-14 rounded-full bg-white border border-gray-200 flex items-center justify-center text-[#047857] font-bold active:scale-95 transition" aria-label="Call JDSN" data-testid="success-call-btn">
              <Phone className="w-5 h-5" />
            </a>
            <a href={`sms:${BRAND.phoneTel}`} className="h-14 rounded-full bg-white border border-gray-200 flex items-center justify-center text-[#047857] font-bold active:scale-95 transition" aria-label="Text JDSN" data-testid="success-sms-btn">
              <MessageSquare className="w-5 h-5" />
            </a>
            <button onClick={share} className="h-14 rounded-full bg-white border border-gray-200 flex items-center justify-center text-[#047857] font-bold active:scale-95 transition" aria-label="Share" data-testid="success-share-btn">
              <Share2 className="w-5 h-5" />
            </button>
          </div>
        </div>

        <p className="mt-6 text-center text-[11px] text-gray-500">
          Questions? Text or call <span className="font-bold text-gray-800">{BRAND.phone}</span> · {BRAND.email}
        </p>
      </div>
    </div>
  );
}

function Row({ label, value, icon }) {
  return (
    <div className="flex items-start justify-between gap-4">
      <div className="text-[10px] uppercase tracking-[0.18em] text-gray-500 font-bold pt-1 min-w-[88px]">{label}</div>
      <div className="flex items-center gap-2 text-right text-sm font-bold text-gray-900 min-w-0">
        {icon}
        <span className="truncate">{value}</span>
      </div>
    </div>
  );
}
